import clsx from "clsx";
import { IconChevronDown } from "@tabler/icons-react";
import { useRouter } from "next/router";
import { useMemo } from "react";
import Link from "next/link";

import {
  BRAND_LOGGED_IN_START_PAGE,
  BRAND_PATHS,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
} from "@chef/constants";
import {
  useAppDispatch,
  useAppSelector,
  useCalendarData,
} from "@chef/state-management/hooks";
import {
  formatDate,
  getNextCutoff,
  getWeekYearCutoffByDate,
  isMealboxBasketType,
  orderHasBeenDelivered,
} from "@chef/helpers";
import { Skeleton } from "@chef/components";
import {
  showMyMenuSelectWeekModal,
  useOrdersQuery,
} from "@chef/state-management";
import { shouldShowDelivery } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";
import { Breakfast, Plate, Cart } from "@chef/icons/large";

import { Logo } from "../Logo";
import { Drawer } from "./Drawer";
import { intl } from "./MyMenuVariant.Intl";

type LinkWithIconProps = {
  isActive: boolean;
  href: string;
  label: string;
  children: React.ReactNode;
};

const LinkWithIcon = ({
  isActive,
  href,
  label,
  children,
}: LinkWithIconProps) => (
  <Link className="flex gap-2 items-center group" href={href}>
    {children}
    <span
      className={clsx(
        "group-hover:underline decoration-2 underline-offset-8 decoration-primary",
        isActive && "underline",
      )}
    >
      {label}
    </span>
  </Link>
);

export const MyMenuVariant = () => {
  const { asPath, query } = useRouter();
  const dispatch = useAppDispatch();
  const { week: nextCutoffWeek, year: nextCutoffYear } = getNextCutoff();
  const { week: queryWeek, year: queryYear } = query;

  const week = queryWeek ? Number(queryWeek) : nextCutoffWeek;
  const year = queryYear ? Number(queryYear) : nextCutoffYear;
  const isOnDeliveryPage = isEqualStrings(asPath, BRAND_PATHS.MY_DELIVERY);

  const handleOpenDialog = () => {
    dispatch(showMyMenuSelectWeekModal());
  };

  const isMyMenuSelectWeekModalOpen = useAppSelector(
    (state) => state.modal.myMenuSelectWeek.open,
  );

  const { calendar, isLoading: isLoadingCalendar } = useCalendarData({
    week: nextCutoffWeek,
    year: nextCutoffYear,
  });

  const { order, isLoading: isLoadingOrder } = useOrdersQuery(
    {
      count: 1,
      orderTypes: [ORDER_TYPE_IDS.RECURRING],
      orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
    },
    {
      selectFromResult: ({ data, isLoading }) => ({
        order:
          data?.orders[0] && shouldShowDelivery(data.orders[0].deliveryDate)
            ? {
                deliveryDate: data.orders[0].deliveryDate,
                hasBeenDelivered: orderHasBeenDelivered(data.orders[0]),
              }
            : undefined,
        isLoading,
      }),
    },
  );

  const deliveryDateMessage = useMemo(() => {
    if (isOnDeliveryPage) {
      return formatDate(
        order?.deliveryDate ?? new Date().getTime(),
        intl.SHORT_DATE_FORMAT,
      );
    }

    const deliveryDate = calendar
      ?.find((cw) => cw.week === week && cw.year === year)
      ?.baskets?.find((basket) => isMealboxBasketType(basket))?.shippingDetails
      .plannedDeliveryInformation.deliveryDate;

    return formatDate(
      new Date(deliveryDate ?? new Date().getTime()),
      intl.SHORT_DATE_FORMAT,
    );

    return;
  }, [order, calendar, week, year, isOnDeliveryPage]);

  const deliveryStatusMessage = useMemo(() => {
    if (isOnDeliveryPage) {
      return order?.hasBeenDelivered ? intl.DELIVERED : intl.IN_TRANSIT;
    }

    const deliveryDate = calendar
      ?.find((cw) => cw.week === week && cw.year === year)
      ?.baskets?.find((basket) => isMealboxBasketType(basket))?.shippingDetails
      .plannedDeliveryInformation.deliveryDate;

    const date = getWeekYearCutoffByDate(
      new Date(deliveryDate ?? new Date().getTime()),
    );

    return `${intl.MAKE_CHANGES_BY} ${formatDate(date, intl.LONG_DATE_FORMAT)}`;
  }, [order, isOnDeliveryPage, calendar, week, year]);

  return (
    <div className="flex flex-col">
      <div
        id="my-menu-header"
        className="flex relative justify-between items-center my-1 w-full h-15 md:my-2"
      >
        <div className="flex shrink">
          <Logo responsive href={BRAND_LOGGED_IN_START_PAGE} />
        </div>

        <div className="flex absolute left-1/2 gap-8 justify-center items-center transform -translate-x-1/2">
          {isLoadingCalendar || isLoadingOrder ? (
            <Skeleton
              animation="pulse"
              height={10}
              width={50}
              variant="rounded"
            />
          ) : (
            <button
              type="button"
              id="delivery-date-button"
              onClick={handleOpenDialog}
              className="flex flex-col items-center ml-2 rounded hover:outline md:hover:outline-none outline-4 outline-grey-2/75 md:p-2 hover:bg-grey-2/75"
            >
              <div className="flex gap-1 justify-center items-center">
                <strong className="text-sm">{deliveryDateMessage}</strong>
                <IconChevronDown
                  size={16}
                  className={clsx(
                    "transition-transform duration-350",
                    isMyMenuSelectWeekModalOpen && "rotate-180",
                  )}
                />
              </div>
              <p className="text-sm text-grey-1 text-nowrap">
                {deliveryStatusMessage}
              </p>
            </button>
          )}
        </div>

        <div className="flex justify-end items-center shrink">
          <Drawer />
        </div>
      </div>
      <div className="hidden justify-between items-center my-2 h-10 md:flex">
        <div className="flex gap-7 items-center">
          <LinkWithIcon
            href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
            label={intl.MEALS}
            isActive={[
              BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
              BRAND_PATHS.MY_MENU_PAGE,
            ].some((link) => isEqualStrings(asPath, link))}
          >
            <Plate className="w-6 h-6" />
          </LinkWithIcon>

          <LinkWithIcon
            href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
            label={intl.ADDONS}
            isActive={isEqualStrings(
              asPath,
              BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
            )}
          >
            <Breakfast className="w-6 h-6" />
          </LinkWithIcon>
        </div>

        <LinkWithIcon
          href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year })}
          label={intl.CART}
          isActive={isEqualStrings(
            asPath,
            BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year }),
          )}
        >
          <Cart className="w-6 h-6" />
        </LinkWithIcon>
      </div>
    </div>
  );
};
